import { useEffect, useState, useMemo } from "react";
import PaymentModal from "../payments/PaymentModal";
import { allowOrWarn, Util } from '../../util/Util';
import { useAuth } from "../../hooks/useAuth";
import { useData } from "../../hooks/useData";
import { useUI } from "../../providers/MoleUIContext";
import { useDateRange, periodRequestConfig } from "../../hooks/useDateRange";
import InvoiceReport from "./InvoiceReport";
import { toast } from "react-toastify";
import { useRef } from "react";
import { Box, IconButton, ListItemIcon, MenuItem, Tooltip } from '@mui/material';
import ConfirmDialog from "../../components/ConfirmDialog";
import { useLocation, useNavigate } from "react-router-dom";
import { LocalStorageKeys, NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from "../../util/constants";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TableView from "../../components/TableView";
import { AddCircleOutline, AssignmentReturn, AttachMoney, ConfirmationNumber, Delete, DeleteOutline, Edit, NoteAdd, ReceiptOutlined, Refresh } from "@mui/icons-material";
import { MRT_ColumnDef } from "material-react-table";
import ExportMenuButton from "../../components/ExportMenuButton";
import { useTranslation } from "react-i18next";
import ReturnSaleModal from "../sale/ReturnSaleModal";
import VoucherModal from "../voucher/VoucherModal";
import { createSignCell } from "../../util/cellFormatters";
import { useAppSocket } from "../../providers/AppSocketContext";
import { parseISODay } from "../../util/businessClock";
import ReceiptDialog from "../../components/ReceiptDialog";
import { ReceiptSource } from "../../util/receiptPdf";

import { roundQty } from "../../util/quantity";
import { canAmend, canDrop, canPrintReceipt, canRecord } from '../../util/access';
interface Props {
	bundle?: any;
	hideColumns?: string[];
	onCallback?: () => void;
	isAgent?: boolean;
}

const InvoiceSituation = ({ bundle, hideColumns, onCallback, isAgent }: Props) => {
	const [toPay, setToPay] = useState<any>(null);
	const [toEdit, setToEdit] = useState<any>(null);
	const [toDelete, setToDelete] = useState<any>(null);
	const [toDeleteBulk, setToDeleteBulk] = useState<any[]>([]);
	const [toView, setToView] = useState<any>(null);
	const [toReturn, setToReturn] = useState<any>(null);
	const [voucherIssue, setVoucherIssue] = useState<{ row: any; type: 'change' | 'credit_note' } | null>(null);
	const [newRecord, setNewRecord] = useState<boolean>(false);
	const [receiptSource, setReceiptSource] = useState<ReceiptSource | null>(null);

	const { api, session, access } = useAuth();
	const { setLoadingMessage, queueReRun } = useUI();
	const { data, lastUpdate, loadFromIndexedDB, saveFromServer, dateRange, loading, setLoading, setData } = useData(LocalStorageKeys.INVSIT_BATCHED_RECORDS);
	const { dateRange: period, setDateRange: setPeriod } = useDateRange(bundle ? undefined : LocalStorageKeys.INVSIT_BATCHED_RECORDS);
	const { t } = useTranslation();
	const { appSettings } = useAppSocket();

	const paymentMade = useRef(false);
	const toastIdRef = useRef<string | number>(-1);
	const tableRef = useRef<any>(null);
	const firstFetchRef = useRef(false);

	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		return () => {
			// Runs just before the component is destroyed
			if (paymentMade.current) {
				onCallback?.();
			}
		};
	}, []);

	useEffect(() => {
		if (!bundle && !loadFromIndexedDB() && period && !firstFetchRef.current) {
			fetchData();
			firstFetchRef.current = true;
		}
	}, [period]);

	useEffect(() => {
		if (bundle) {
			fetchData();
			firstFetchRef.current = true;
		}
	}, [bundle]);

	useEffect(() => {
		if (location.state?.changes) {
			toastIdRef.current = toast.loading(`${t("common:toast.success.changes")} ${t("common:toast.loading.refresh")}`);
			fetchData(false);
		}
	}, [location.state]);

	const fetchData = async (useLoadModal: boolean = true) => {
		setLoading(true);

		const res = await api.get(`/invoices/situations/${bundle ? (isAgent ? "agents/" : "clients/") + (isAgent ? bundle.name : bundle.name) : ""}`, {
			...(!bundle && periodRequestConfig(period))
		});

		if (res) {
			if (res.status === 200) {
				if (bundle) {
					setData(res.data);
				} else {
					saveFromServer(res.data, period);
				}
				toast.update(toastIdRef.current, {
					render: t("common:toast.success.refresh"),
					type: "success",
					isLoading: false,
					autoClose: 3000,
				});
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else {
				console.error("Failed to fetch data:", res);
				toast.dismiss(toastIdRef.current);
			}
		} else {
			toast.dismiss(toastIdRef.current);
		}

		setLoading(false);
	};

	const handlePaymentSuccess = () => {
		if (!paymentMade.current) {
			paymentMade.current = true;
		}
		toastIdRef.current = toast.loading(t("common:toast.loading.paymentSuccess"));
		fetchData();
	};

	// Define table columns
	const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
		{
			header: t("common:tables.date"),
			id: "date",
			accessorFn: (row: any) => parseISODay(row.date),
			Cell: ({ cell }: { cell: any }) => Util.formatDate(cell.getValue(), "medium"), //render Date as a string
			filterVariant: 'date-range',
		},
		{
			header: t("common:tables.agent"),
			accessorKey: "agent",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.client"),
			accessorKey: "client",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.type"),
			accessorKey: "inv_type",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.amount"),
			accessorFn: (row: any) => Number(row.amount),
			Cell: createSignCell({ signStyle: "accounting" }),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum', //show the max age in the group (lots of pre-built aggregationFns to choose from)
			//required to render an aggregated cell
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box
						sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}
					>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			// Add Footer for Grand Total
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);
				const [isCalculating, setIsCalculating] = useState(false);

				useEffect(() => {
					setIsCalculating(true);
					setTotal(0);

					const rows = table.getFilteredRowModel().rows;
					// run in a micro delay, allowing paint before heavy computation
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.amount);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
						setIsCalculating(false);
					}, 0); // <— yields to browser first

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			header: t("common:tables.paidRefunded"),
			accessorFn: (row: any) => Number(row.paid),			
			Cell: createSignCell({ signStyle: "accounting" }),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum', //show the max age in the group (lots of pre-built aggregationFns to choose from)
			//required to render an aggregated cell
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box
						sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}
					>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			// Add Footer for Grand Total
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);
				const [isCalculating, setIsCalculating] = useState(false);

				useEffect(() => {
					setIsCalculating(true);
					setTotal(0);

					const rows = table.getFilteredRowModel().rows;
					// run in a micro delay, allowing paint before heavy computation
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.paid);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
						setIsCalculating(false);
					}, 0); // <— yields to browser first

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			header: t("common:tables.owing"),
			accessorFn: (row: any) => Number(row.balance),
			Cell: createSignCell({ signStyle: "accounting" }),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum', //show the max age in the group (lots of pre-built aggregationFns to choose from)
			//required to render an aggregated cell
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box
						sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}
					>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			// Add Footer for Grand Total
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);
				const [isCalculating, setIsCalculating] = useState(false);

				useEffect(() => {
					setIsCalculating(true);
					setTotal(0);

					const rows = table.getFilteredRowModel().rows;
					// run in a micro delay, allowing paint before heavy computation
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.balance);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
						setIsCalculating(false);
					}, 0); // <— yields to browser first

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);


				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			header: t("common:tables.invoiceNo"),
			accessorKey: "reference",
			Cell: ({ cell }: { cell: any }) => (
				<button
					style={{ background: "none", border: "none", padding: 0, color: "inherit", cursor: "pointer", textDecoration: "underline" }}
					onClick={() => setToView(cell.row.original)}
				>
					{cell.getValue()}
				</button>
			),
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.user"),
			accessorKey: "user",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.pos"),
			id: "depot",
			accessorFn: (row: any) => row.depot || row.pos_name || "",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
	], [t]);

	const handleDelete = async () => {
		if (toDelete) {
			setLoadingMessage("DELETE");

			const res = await api.delete(`/sales/${toDelete.batch}`);

			if (res.status === 204) {
				toastIdRef.current = toast.loading(`${t("common:toast.success.delete")} ${t("common:toast.loading.refresh")}`);
				fetchData();
			} else if (res.status === 409) {
				toast.error(t("common:toast.error.delete"), { autoClose: 3000 });
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(handleDelete);
			}

			setLoadingMessage(null);
			setToDelete(null);
		}
	};

	const handleBulkDelete = async () => {
		if (toDeleteBulk.length > 0) {
			const payload = { batches: toDeleteBulk };

			setLoadingMessage("DELETE");

			const res = await api.post("/sales/delete", payload);

			if (res) {
				if (res.status === 204) {
					toastIdRef.current = toast.loading(`${t("common:toast.success.delete")} ${t("common:toast.loading.refresh")}`);
					tableRef.current?.resetRowSelection();
					fetchData();
				} else if (res.status === 409) {
					toast.error(t("common:toast.error.delete"), { autoClose: 3000 });
				} else if (res.status === 401 || res.status === 422) {
					queueReRun(handleBulkDelete);
				}
			}

			setLoadingMessage(null);
			setToDeleteBulk([]);
		}
	};

	return (
		<>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<TableView
					stateKey={bundle ? undefined : LocalStorageKeys.INVSIT_BATCHED_RECORDS}
					header={t("nav:tabs.invoice_situation")}
					data={data}
					columns={columns}
					totals={[
						{ key: "amount", label: t("common:tables.amount") },
						{ key: "paid", label: t("common:tables.paidRefunded") },
						{ key: "balance", label: t("common:tables.owing") },
					]}
					lastUpdated={lastUpdate}
					period={dateRange}
					setDateRangeFilter={setPeriod}
					isLoading={loading}
					bundle={bundle}
					hideColumns={hideColumns}
					getRowSx={(row) => Util.compareStrings(row.original?.inv_type || "", "Return") ? {
						backgroundColor: (theme: any) => theme.palette.mode === 'dark'
							? 'rgba(255, 167, 38, 0.12)'
							: 'rgba(255, 167, 38, 0.08)',
						borderLeft: '3px solid',
						borderLeftColor: 'warning.main',
					} : {}}

					renderTopToolbarActions={(table) => (
						<Box sx={{ display: 'flex', gap: '0.5rem' }}>
							<Tooltip title={t("common:ui.tooltips.refreshData")}>
								<IconButton onClick={() => fetchData()} color="primary">
									<Refresh />
								</IconButton>
							</Tooltip>
							<Tooltip title={t("common:ui.tooltips.newPayment")}>
								<IconButton
									onClick={() => {
										if (allowOrWarn(canRecord(access, 'payment')) || allowOrWarn(canRecord(access, 'refund'))) {
											setNewRecord(true);
										}
									}}
									color="success">
									<AddCircleOutline />
								</IconButton>
							</Tooltip>
							{table.getSelectedRowModel().flatRows.length === 1
								&& Util.compareStrings(
									table.getSelectedRowModel().flatRows[0].original.inv_type,
									"Sale",
								) && (
								<Tooltip title={t("common:ui.tooltips.editSelectedInvoice")}>
									<IconButton
										onClick={() => {
											if (allowOrWarn(canAmend(access, 'sale'))) {
												navigate("/pos", {
													state: {
														payload: table.getSelectedRowModel().flatRows[0].original,
														src: location.pathname,
													},
												});
											}
										}}
										color="primary"
									>
										<Edit />
									</IconButton>
								</Tooltip>
							)}
							{[
								...new Set(
									table.getSelectedRowModel().flatRows.map((row: any) => row.original.batch)
								),
							].length === 1 && Util.compareStrings("Sale", table.getSelectedRowModel().flatRows[0].original.inv_type) && (
									<Tooltip title={t("common:ui.tooltips.returnSelectedInvoice")}>
										<IconButton
											onClick={() => {
												if (allowOrWarn(canRecord(access, 'return'))) {
													setToReturn(table.getSelectedRowModel().flatRows[0].original);
												}
											}}
											color="warning">
											<AssignmentReturn />
										</IconButton>
									</Tooltip>
								)}
							{table.getSelectedRowModel().flatRows.length > 0 && (
								<Tooltip title={t("common:ui.tooltips.deleteSelection")}>
									<IconButton
										onClick={() => {
											if (allowOrWarn((canDrop(access, 'sale', 'delete_bulk') || canDrop(access, 'return', 'delete_bulk')))) {
												const rowsBatches = [
													...new Set(
														table.getSelectedRowModel().flatRows.map((row: any) => row.original.batch)
													),
												];
												if (rowsBatches.length === 1) {
													setToDelete(table.getSelectedRowModel().flatRows[0].original);
												} else {
													tableRef.current = table;

													setToDeleteBulk(rowsBatches);
												}
											}
										}}
										color="error">
										<Delete />
									</IconButton>
								</Tooltip>
							)}
							<ExportMenuButton
								prefix="invoice_situation"
								data={data}
								columns={columns}
								table={table}
							/>
						</Box>
					)}
					renderRowActionMenuItems={(row, closeMenu) => [
						!(toDelete || toEdit || toReturn) && Number(row.original.balance) !== 0 ? (
							<MenuItem
								key="pay"
								onClick={() => {
									if (Util.compareStrings("Sale", row.original.inv_type) && allowOrWarn(canRecord(access, 'payment'))) {
										setToPay(row.original);
									} else if (Util.compareStrings("Return", row.original.inv_type) && allowOrWarn(canRecord(access, 'refund'))) {
										setToPay(row.original);
									}
									closeMenu();
								}}
								sx={{
									color: 'success.main',
								}}
							>
								<ListItemIcon sx={{
									color: 'success.main',
								}}>
									<AttachMoney />
								</ListItemIcon>
								{Util.compareStrings("Sale", row.original.inv_type) ? t("common:ui.buttons.pay") : t("common:ui.buttons.refund")}
							</MenuItem>
						) : null,
						!(toDelete || toEdit || toReturn) && Util.compareStrings("Sale", row.original.inv_type) ? (
							<MenuItem
								key="edit"
								onClick={() => {
									if (allowOrWarn(canAmend(access, 'sale'))) {
										navigate("/pos", { state: { payload: row.original, src: location.pathname } });
									}
									closeMenu();
								}}
								sx={{
									color: 'primary.main',
								}}
							>
								<ListItemIcon sx={{
									color: 'primary.main',
								}}>
									<Edit />
								</ListItemIcon>
								{t("common:ui.buttons.edit")}
							</MenuItem>
						) : null,
						!(toDelete || toEdit || toReturn) && Util.compareStrings(String(row.original.inv_type || ''), "Sale") && !String(row.original.batch || "").startsWith("ret:") ? (
							<MenuItem
								key="return"
								onClick={() => {
									if (allowOrWarn(canRecord(access, 'return'))) {
										setToReturn(row.original);
									}
									closeMenu();
								}}
								sx={{
									color: 'warning.main',
								}}
							>
								<ListItemIcon sx={{
									color: 'warning.main',
								}}>
									<AssignmentReturn />
								</ListItemIcon>
								{t("common:ui.buttons.return")}
							</MenuItem>
						) : null,
						!(toDelete || toEdit || toReturn) && Number(row.original.balance) === 0 && Util.compareStrings(String(row.original.inv_type || ''), "Sale") ? (
							<MenuItem
								key="change-voucher"
								onClick={() => {
									if (allowOrWarn(canRecord(access, 'voucher'))) {
										setVoucherIssue({ row: row.original, type: 'change' });
									}
									closeMenu();
								}}
								sx={{ color: 'success.main' }}
							>
								<ListItemIcon sx={{ color: 'success.main' }}>
									<ConfirmationNumber />
								</ListItemIcon>
								{t("common:voucher.issueChange")}
							</MenuItem>
						) : null,
						!(toDelete || toEdit || toReturn) && Number(row.original.balance) !== 0 && Util.compareStrings(String(row.original.inv_type || ''), "Return") ? (
							<MenuItem
								key="credit-note"
								onClick={() => {
									if (allowOrWarn(canRecord(access, 'voucher'))) {
										setVoucherIssue({ row: row.original, type: 'credit_note' });
									}
									closeMenu();
								}}
								sx={{ color: 'success.main' }}
							>
								<ListItemIcon sx={{ color: 'success.main' }}>
									<NoteAdd />
								</ListItemIcon>
								{t("common:voucher.issueCreditNote")}
							</MenuItem>
						) : null,
						!(toDelete || toEdit || toReturn)
							&& Util.compareStrings("Sale", row.original.inv_type)
							&& Number(row.original.paid) > 0
							&& canPrintReceipt(access) ? (
							<MenuItem
								key="receipt"
								onClick={() => {
									setReceiptSource({ kind: "invoice", invoice: row.original.batch, reference: row.original.reference });
									closeMenu();
								}}
							>
								<ListItemIcon>
									<ReceiptOutlined />
								</ListItemIcon>
								{t("common:receipt.paymentTitle")}
							</MenuItem>
						) : null,
						!(toDelete || toEdit || toReturn) ? (
							<MenuItem
								key="delete"
								onClick={() => {
									if (allowOrWarn((canDrop(access, 'sale') || canDrop(access, 'return')))) {
										setToDelete(row.original);
									}
									closeMenu();
								}}
								sx={{
									color: 'error.main',
								}}
							>
								<ListItemIcon sx={{
									color: 'error.main',
								}}>
									<Delete />
								</ListItemIcon>
								{t("common:ui.buttons.delete")}
							</MenuItem>
						) : null,
					]}
				/>

			</LocalizationProvider>

			<ReceiptDialog source={receiptSource} onClose={() => setReceiptSource(null)} />

			{/* Payment Modal */
				(newRecord || !!toPay) &&
				<PaymentModal
					isOpen={newRecord || !!toPay}
					toggle={() => { setNewRecord(false); setToPay(null); }}
					bundle={toPay}
					onSuccess={handlePaymentSuccess}
				/>
			}

			{/* Invoice Modal */
				toView &&
				<InvoiceReport
					isOpen={!!toView}
					toggle={() => setToView(null)}
					bundle={toView}
				/>
			}

			{/* Return Sale Modal */
				toReturn &&
				<ReturnSaleModal
					isOpen={!!toReturn}
					toggle={() => setToReturn(null)}
					bundle={toReturn}
					onSuccess={() => fetchData()}
				/>
			}

			<VoucherModal
				isOpen={!!voucherIssue}
				preset={voucherIssue ? {
					type: voucherIssue.type,
					amount: voucherIssue.type === 'credit_note'
						? Math.abs(Number(voucherIssue.row.balance || 0))
						: Math.max(0, Math.abs(Number(voucherIssue.row.paid || 0)) - Math.abs(Number(voucherIssue.row.amount || 0))),
					issuee: String(voucherIssue.row.client || ''),
					batch: String(voucherIssue.row.batch || ''),
					invoice: String(voucherIssue.row.batch || ''),
					pos: Number(voucherIssue.row.pos) || undefined,
					pos_name: String(voucherIssue.row.pos_name || ''),
					description: String(voucherIssue.row.reference || voucherIssue.row.batch || ''),
					lockType: true,
					lockPos: true,
					allowChange: voucherIssue.type === 'change',
				} : null}
				onClose={() => setVoucherIssue(null)}
				onSuccess={() => {
					setVoucherIssue(null);
					fetchData();
				}}
			/>

			{/* Confirmation Modal: no conditional rendering for perfomance as it is lightweight */
				<ConfirmDialog
					onConfirm={toDelete ? handleDelete : handleBulkDelete}
					onCancel={() => { toDelete ? setToDelete(null) : setToDeleteBulk([]) }}
					isOpen={toDelete || toDeleteBulk.length > 0}
					setIsOpen={() => { }}
					title={<><DeleteOutline sx={{ color: "red" }} /> {t("common:dialog.deleteConfirmation")}</>}
					message={
						toDelete ?
							<>
								{t("common:sales.confirm.deleteSingleStart")}&nbsp;
								<b><u>{Util.formatCurrency(toDelete?.amount, appSettings?.BIZ_CURRENCY?.value || "USD")}</u></b>&nbsp;
								{t("common:sales.confirm.deleteSingleTo")} <b><u>{toDelete?.client}</u></b>&nbsp;
								{t("common:sales.confirm.deleteSingleAndPayments")}&nbsp;
								{t("common:sales.confirm.deleteSingleDate")} <b><u>{Util.formatDate(toDelete?.date, "full")}</u></b>&nbsp;
								, {t("common:sales.confirm.deleteSingleReference")} <button
									style={{ background: "none", border: "none", padding: 0, color: "inherit", cursor: "pointer", textDecoration: "underline" }}
									onClick={() => setToView(toDelete)}
								>
									<b><u>{toDelete?.reference}</u></b>
								</button>!
							</>
							:
							<>
								{t("common:sales.confirm.deleteBulkWithPayments", { count: toDeleteBulk.length })}
							</>
					}
					confirmBtnColor="error"
					confirmBtnVariant="contained"
					confirmBtnText={<>{t("common:ui.buttons.delete")}</>}
				/>
			}

		</>
	);
};

export default InvoiceSituation;
